/* eslint-disable no-restricted-globals */
import React, { lazy, Suspense, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedPermissionRoute from './components/ProtectedPermissionRoute';
import AuthRoute from './components/AuthRoute';

import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import 'primereact/resources/themes/md-light-indigo/theme.css';
import './App.scss';

import Login from './views/Login/Login';
import HelpCenterPublic from './views/HelpCenter/HelpCenter';
import SuspenseLoader from './components/SuspenseLoader/SuspenseLoader';
import Home from './views/Home/Home';
import { StatusProvider } from './hooks/useStatus';
import CopilotPanel from './components/Copilot/CopilotPanel'; // COPILOT POC — remove with the block below
import MeetingPrepPanel from './components/MeetingPrep/MeetingPrepPanel'; // MEETING-PREP — remove with this line + its mount
import DueDateManagement from './views/Settings/DueDateManagement/DueDateManagement';
import DueDateForm from './views/Settings/DueDateManagement/DueDateForm/DueDateForm';
// import SSOConfigManagement from './views/Settings/SSOConfigManagement/SSOConfigManagement';

const Unauthorized = lazy(() => import('./components/Unauthorized/Unauthorized'));
const AIPlayground = lazy(() => import('./views/AIPlayground/AIPlayground'));
const JobsHistory = lazy(() => import('./views/AIPlayground/JobsHistory/JobsHistory'));
const AIJob = lazy(() => import('./views/AIPlayground/AIJob/AIJob'));
const Assets = lazy(() => import('./views/Assets/Assets'));
const Signup = lazy(() => import('./views/Signup/Signup'));
const ForgotPassword = lazy(() => import('./views/ForgotPassword/ForgotPassword'));
const ChangePassword = lazy(() => import('./views/ChangePassword/ChangePassword'));
const SsoAuthRedirectSuccess = lazy(() => import('./views/SsoAuthRedirectSuccess/SsoAuthRedirectSuccess'));
const SearchResult = lazy(() => import('./views/SearchResult/SearchResult'));
const HelpCenter = lazy(() => import('./views/HelpCenter/HelpCenter'));
const Workflows = lazy(() => import('./views/Workflows/Workflows'));
const WorkflowsView = lazy(() => import('./views/Workflows/WorkflowsView/WorkflowsView'));
const EdamIntegrationStatus = lazy(() => import('./views/Workflows/EdamIntegrationStatus/EdamIntegrationStatus'));
const WorkflowProgress = lazy(() => import('./views/WorkflowProgress/WorkflowProgress'));
const WorkflowHistory = lazy(() => import('./views/WorkflowProgress/WorkflowHistory/WorkflowHistory'));
const AnStudio = lazy(() => import('./views/AnnotationStudio/AnnotationStudio'));

const Settings = lazy(() => import('./views/Settings/Settings'));
const Profile = lazy(() => import('./views/Profile/profile'));
const LoginLandingPageConfig = lazy(() => import('./views/Profile/LoginLandingConfig/LoginLandingPageConfig'));
const Configuration = lazy(() => import('./views/Configuration/Configuration'));
const SSOConfiguration = lazy(() => import('./views/Configuration/SSOConfigManagement/SSOConfigManagement'));
const ServiceOutage = lazy(() => import('./views/Configuration/ServiceOutage/ServiceOutage'));
const UserManagement = lazy(() => import('./views/Settings/UserManagement/UserManagement'));
const UserForm = lazy(() => import('./views/Settings/UserManagement/UserForm/UserForm'));
const BrandManagement = lazy(() => import('./views/Settings/BrandManagement/BrandManagement'));
const BrandForm = lazy(() => import('./views/Settings/BrandManagement/BrandForm/BrandForm'));
const AssetManagement = lazy(() => import('./views/Settings/AssetTypeManagement/AssetTypeManagement'));
const AssetForm = lazy(() => import('./views/Settings/AssetTypeManagement/AssetTypeForm/AssetTypeForm'));
const RoleManagement = lazy(() => import('./views/Settings/RoleManagement/RoleManagement'));
const RoleForm = lazy(() => import('./views/Settings/RoleManagement/RoleForm/RoleForm'));
const GroupManagement = lazy(() => import('./views/Settings/GroupManagement/GroupManagement'));
const GroupForm = lazy(() => import('./views/Settings/GroupManagement/GroupForm/GroupForm'));
const DelegationManagement = lazy(() => import('./views/Settings/DelegationManagement/DelegationManagement'));
const DelegationForm = lazy(() => import('./views/Settings/DelegationManagement/DelegationForm/DelegationForm'));
const ReviewerLabelManagement = lazy(() => import('./views/Settings/ReviewerLabelManagement/ReviewerLabelManagement'));
const ReviewerLabelForm = lazy(() => import('./views/Settings/ReviewerLabelManagement/ReviewerLabelForm/ReviewerLabelForm'));
const AgencyManagement = lazy(() => import('./views/Settings/AgencyManagement/AgencyManagement'));
const AgencyForm = lazy(() => import('./views/Settings/AgencyManagement/AgencyForm/AgencyForm'));
const TargetAudienceManagement = lazy(() => import('./views/Settings/TargetAudience/TargetAudienceManagement'));
const TargetAudienceForm = lazy(() => import('./views/Settings/TargetAudience/TargetAudienceForm/TargetAudienceForm'));
const UserRoleColorConfig = lazy(() => import('./views/Settings/UserRoleColorConfig/UserRoleColorConfig'));
const AIPortal = lazy(() => import('./views/AIPortal/AIPortal'));
const Collection = lazy(() => import('./views/AIPortal/Collection/Collection'));
const AddCollection = lazy(() => import('./views/AIPortal/Collection/AddCollection/AddCollection'));
const AddCollectionFromStudy = lazy(() => import('./views/AIPortal/Collection/AddCollectionFromStudy/AddCollectionFromStudy'));
const Analysis = lazy(() => import('./views/AIPortal/Analysis/Analysis'));
const AddAnalysis = lazy(() => import('./views/AIPortal/Analysis/AddAnalysis/AddAnalysis'));
const AddAIStudy = lazy(() => import('./views/AIPortal/Analysis/AddAIStudy/AddAIStudy'));
const GeneratedRules = lazy(() => import('./views/AIPortal/Analysis/GeneratedRules/GeneratedRules'));

const Reports = lazy(() => import('./views/Reports/Reports'));
const Analytics = lazy(() => import('./views/Reports/Analytics/Analytics'));
const Reporting = lazy(() => import('./views/Reports/Reporting/Reporting'));
const ReviewVolume = lazy(() => import('./views/Reports/Reporting/ReviewVolume/ReviewVolume'));
const DueDateCompliance = lazy(() => import('./views/Reports/Reporting/DueDateCompliance/DueDateCompliance'));
const ReviewerPerformance = lazy(() => import('./views/Reports/Reporting/ReviewerPerformance/ReviewerPerformance'));

const AllNotifications = lazy(() => import('./views/AllNotifications/AllNotifications'));
const NotificationSettings = lazy(() => import('./views/NotificationSettings/NotificationSettings'));

const Wrapper = ({ children }: any) => {
  const location = useLocation();
  useLayoutEffect(() => {
    document.documentElement.scrollTo(0, 0);
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return children;
};

function App() {
  return (
    <div className="app-main" role="main">
      <Suspense fallback={<SuspenseLoader />}>
        <Wrapper>
          <Routes>
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<Home />} />
              <Route path="/search-result" element={<SearchResult />} />

              <Route element={<ProtectedPermissionRoute requiredPermission="menu_dashboard" />}>
                <Route path="/home" element={<Home />} />
              </Route>

              <Route element={<ProtectedPermissionRoute requiredPermission="menu_profile" />}>
                <Route path="/profile" element={<Profile />}>
                  <Route path="/profile/login-landing-page-config" element={<LoginLandingPageConfig />} />
                </Route>
              </Route>

              <Route element={<ProtectedPermissionRoute requiredPermission="menu_aiplayground" />}>
                <Route path="/ai-playground" element={<AIPlayground />} />
                <Route path="/ai-playground/history" element={<JobsHistory />} />
                <Route path="/ai-playground/job/:jobId" element={<AIJob />} />
              </Route>

              <Route element={<ProtectedPermissionRoute requiredPermission="menu_assets" />}>
                <Route path="/assets-library" element={<Assets />} />
              </Route>

              <Route element={<ProtectedPermissionRoute requiredPermission="menu_workflows" />}>
                <Route path="/workflows" element={<Workflows />}>
                  <Route path="list" element={<WorkflowsView />} />
                  <Route path="materials" element={<WorkflowsView />} />
                </Route>
                <Route path="/workflows/edam-integration-status" element={<EdamIntegrationStatus />} />
              </Route>

              <Route element={<ProtectedPermissionRoute requiredPermission="menu_workflows" />}>
                <Route path="/annotation-studio" element={<AnStudio />} />
                <Route path="/annotation-studio/:workflowId/:assetId" element={<StatusProvider><AnStudio /></StatusProvider>} />
                <Route path="/workflow/edit/:workflowId" element={<StatusProvider><WorkflowProgress /></StatusProvider>} />
                <Route path="/workflow/history/:workflowId" element={<StatusProvider><WorkflowHistory /></StatusProvider>} />
              </Route>

              <Route element={<ProtectedPermissionRoute requiredPermission="create_workflow" />}>
                <Route path="/workflow/add" element={<StatusProvider><WorkflowProgress /></StatusProvider>} />
                <Route path="/workflow/create/:assetId/:assetVersion" element={<StatusProvider><WorkflowProgress /></StatusProvider>} />
              </Route>

              <Route path="/ai-portal" element={<AIPortal />}>
                <Route element={<ProtectedPermissionRoute requiredPermission="ai_privileges" />}>
                  <Route path="/ai-portal/collection" element={<Collection />} />
                  <Route path="/ai-portal/collection/add" element={<AddCollection />} />
                  <Route path="/ai-portal/collection/add-from-study" element={<AddCollectionFromStudy />} />
                  <Route path="/ai-portal/collection/edit/:collectionId" element={<AddCollection key={location.pathname} />} />
                  <Route path="/ai-portal/analysis" element={<Analysis />} />
                  <Route path="/ai-portal/analysis/add" element={<AddAnalysis />} />
                  <Route path="/ai-portal/analysis/addAIStudy" element={<AddAIStudy />} />
                  <Route path="/ai-portal/analysis/addAIStudy/:studyId" element={<AddAIStudy />} />
                  <Route path="/ai-portal/analysis/rules/:collectionId" element={<GeneratedRules />} />
                </Route>
              </Route>
              <Route path="/configuration" element={<Configuration />}>
                <Route path="/configuration/sso-configuration" element={<SSOConfiguration />} />
                <Route path="/configuration/service-outage" element={<ServiceOutage />} />
              </Route>

              <Route path="/settings" element={<Settings />}>
                <Route element={<ProtectedPermissionRoute requiredPermission="manage_users" />}>
                  <Route path="/settings/user-management" element={<UserManagement />} />
                  <Route path="/settings/user-management/add" element={<UserForm />} />
                  <Route path="/settings/user-management/edit/:userid" element={<UserForm />} />
                </Route>
                <Route element={<ProtectedPermissionRoute requiredPermission="manage_brands" />}>
                  <Route path="/settings/brand-management" element={<BrandManagement />} />
                  <Route path="/settings/brand-management/add" element={<BrandForm />} />
                  <Route path="/settings/brand-management/edit/:brandid" element={<BrandForm />} />
                </Route>
                <Route element={<ProtectedPermissionRoute requiredPermission="manage_roles" />}>
                  <Route path="/settings/role-management" element={<RoleManagement />} />
                  <Route path="/settings/role-management/add" element={<RoleForm />} />
                  <Route path="/settings/role-management/edit/:roleid" element={<RoleForm />} />
                </Route>
                <Route element={<ProtectedPermissionRoute requiredPermission="manage_groups" />}>
                  <Route path="/settings/group-management" element={<GroupManagement />} />
                  <Route path="/settings/group-management/add" element={<GroupForm />} />
                  <Route path="/settings/group-management/edit/:groupid" element={<GroupForm />} />
                </Route>
                <Route element={<ProtectedPermissionRoute requiredPermission="manage_delegations" />}>
                  <Route path="/settings/delegation-management" element={<DelegationManagement />} />
                  <Route path="/settings/delegation-management/add" element={<DelegationForm />} />
                  <Route path="/settings/delegation-management/edit/:delegationid" element={<DelegationForm />} />
                </Route>
                <Route element={<ProtectedPermissionRoute requiredPermission="manage_reviewer_label" />}>
                  <Route path="/settings/reviewer-label-management" element={<ReviewerLabelManagement />} />
                  <Route path="/settings/reviewer-label-management/add" element={<ReviewerLabelForm />} />
                  <Route path="/settings/reviewer-label-management/edit/:labelid" element={<ReviewerLabelForm />} />
                </Route>
                <Route element={<ProtectedPermissionRoute requiredPermission="manage_due_date" />}>
                  <Route path="/settings/due-date-management" element={<DueDateManagement />} />
                  <Route path="/settings/due-date-management/add" element={<DueDateForm />} />
                  <Route path="/settings/due-date-management/edit/:duedateid" element={<DueDateForm />} />
                </Route>
                <Route element={<ProtectedPermissionRoute requiredPermission="manage_asset" />}>
                  <Route path="/settings/asset-type-management" element={<AssetManagement />} />
                  <Route path="/settings/asset-type-management/add" element={<AssetForm />} />
                  <Route path="/settings/asset-type-management/edit/:mediaplatformid" element={<AssetForm />} />
                </Route>
                {/* <Route element={<ProtectedPermissionRoute requiredPermission="config_sso" />}>
                  <Route path="/settings/SSOConfigManagement" element={<SSOConfigManagement />} />
                </Route> */}
                <Route element={<ProtectedPermissionRoute requiredPermission="manage_agency" />}>
                  <Route path="/settings/agency-management" element={<AgencyManagement />} />
                  <Route path="/settings/agency-management/add" element={<AgencyForm />} />
                  <Route path="/settings/agency-management/edit/:agencyid" element={<AgencyForm />} />
                </Route>
                <Route element={<ProtectedPermissionRoute requiredPermission="manage_global_colors" />}>
                  <Route path="/settings/user-role-color-config" element={<UserRoleColorConfig />} />
                </Route>
                <Route element={<ProtectedPermissionRoute requiredPermission="manage_agency" />}>
                  <Route path="/settings/target-audience" element={<TargetAudienceManagement />} />
                  <Route path="/settings/target-audience/add" element={<TargetAudienceForm />} />
                  <Route path="/settings/target-audience/edit/:targetaudienceid" element={<TargetAudienceForm />} />
                </Route>
              </Route>

              <Route path="/reports" element={<Reports />}>
                <Route element={<ProtectedPermissionRoute requiredPermission="menu_reports" />}>
                  <Route path="/reports/analytics" element={<Analytics />} />
                  <Route path="/reports/reporting" element={<Reporting />} />
                  <Route path="/reports/reporting/review-volume" element={<ReviewVolume />} />
                  <Route path="/reports/reporting/due-date-compliance" element={<DueDateCompliance />} />
                  <Route path="/reports/reporting/reviewer-performance" element={<ReviewerPerformance />} />
                </Route>
              </Route>

              <Route path="/all-notifications" element={<AllNotifications />} />
              <Route path="/notification-settings" element={<NotificationSettings />} />
              {/* Unauthorized Page */}
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="/help-center" element={<HelpCenter />} />
            </Route>

            <Route element={<AuthRoute />}>
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ForgotPassword />} />
              <Route path="/reset_password_submit" element={<ChangePassword />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/sso-auth" element={<SsoAuthRedirectSuccess />} />
            </Route>

            <Route path="/help-support" element={<HelpCenterPublic />} />
          </Routes>
        </Wrapper>
      </Suspense>
      <CopilotPanel />
      <MeetingPrepPanel />
    </div>
  );
}

export default App;
